import React, { useState, useMemo } from "react";
import Modal from "@/components/controls/Modal";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import SingleSelect from "@/components/controls/SingleSelect";
import { UserData, UserStatus } from "@/entities/users/types";
import { useEntitySearch } from "@/services/hooks/entities/useEntitySearch";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { useTranslation } from "react-i18next";
import UserAvatar from "@/components/controls/UserAvatar";

const TRANSLATION_KEY = "settings.userManagement.changeReportingManagerModal";

interface ChangeReportingManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedUsers: UserData[];
  onApplyChanges: (newManagerId: string) => void;
}

const ChangeReportingManagerModal: React.FC<
  ChangeReportingManagerModalProps
> = ({ isOpen, onClose, selectedUsers, onApplyChanges }) => {
  const { t } = useTranslation();
  const [selectedManagerId, setSelectedManagerId] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");

  const filters = useMemo(() => {
    const baseFilters: any = {
      status: [UserStatus.ACTIVE]
    };

    if (selectedUsers.length > 0) {
      if (selectedUsers.length === 1) {
        baseFilters.id = { ne: selectedUsers[0].id };
      } else {
        baseFilters.id = { not_in: selectedUsers.map((user) => user.id) };
      }
    }

    return baseFilters;
  }, [selectedUsers]);

  const { options: availableManagers, isLoading } = useEntitySearch({
    entityType: EntityType.USER,
    searchTerm,
    filters
  });

  const handleApplyChanges = () => {
    if (selectedManagerId) {
      onApplyChanges(selectedManagerId);
      onClose();
      setSelectedManagerId("");
    }
  };

  const handleCancel = () => {
    onClose();
    setSelectedManagerId("");
  };

  return (
    <Modal
      open={isOpen}
      setIsOpen={(open) => !open && onClose()}
      title={<Text variant="title3H1">{t(`${TRANSLATION_KEY}.title`)}</Text>}
      content={
        <div className="flex flex-col gap-6 p-6">
          <Text variant="body">
            {t(`${TRANSLATION_KEY}.description`, {
              count: selectedUsers.length
            })}
          </Text>

          <div className="flex items-center justify-between gap-4">
            <Text variant="body" className="min-w-0">
              {t(`${TRANSLATION_KEY}.newReportingManager`)}
            </Text>
            <div className="w-[314px]">
              <SingleSelect
                fieldName="new-reporting-manager"
                options={availableManagers}
                selectedValue={selectedManagerId}
                onSelection={(value) => setSelectedManagerId(value as string)}
                placeholder={t(`${TRANSLATION_KEY}.select`)}
                variant="form"
                showSearch={true}
                className="w-full"
                popoverClassName="w-[314px]"
                onSearch={setSearchTerm}
                isLoading={isLoading}
                renderIcon={(userId: string | number) => {
                  const option = availableManagers.find(
                    (opt) => opt.id === userId
                  );
                  const user = option?.userData as UserData;
                  return user ? (
                    <UserAvatar
                      userData={user}
                      size="M"
                      fallbackClassName="bg-gradient-to-t from-slate-600 to-tertiary text-xs"
                    />
                  ) : (
                    <div className="size-6" />
                  );
                }}
              />
            </div>
          </div>
        </div>
      }
      footer={
        <div className="flex items-center justify-end gap-3">
          <Button variant="secondaryNoBg" onClick={handleCancel}>
            {t("cancel")}
          </Button>
          <Button onClick={handleApplyChanges} disabled={!selectedManagerId}>
            {t(`${TRANSLATION_KEY}.applyChanges`)}
          </Button>
        </div>
      }
      contentClassname="max-w-2xl"
      hideDivider={true}
    />
  );
};

export default ChangeReportingManagerModal;
